import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloLink } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { getRequestId } from '../Sentry';
import { useAuth } from 'react-oidc-context';
import { ApolloProvider, useApolloClient } from 'react-apollo';
import { useHistory } from 'react-router';
import { PropsWithChildren, createContext, useContext } from 'react';
import { getCachedAuthContext } from './AuthWrapper';

const metaTag = document.querySelector('meta[name="API_URL"]');
const metaUrl = metaTag ? metaTag.getAttribute('content') : undefined;

const federationGatewayTag = document.querySelector('meta[name="FEDERATION_GATEWAY_URL"]');
const federationMetaUrl = federationGatewayTag
  ? federationGatewayTag.getAttribute('content')
  : undefined;

const legacyGatewayUrl = `${metaUrl || process.env.API_URL}/graphql`;
const federationGatewayUrl = `${federationMetaUrl || process.env.FEDERATION_GATEWAY_URL}/graphql`;

export type ApolloClientsContextType = {
  legacyGatewayClient: ReturnType<typeof useApolloClient>;
  federationGatewayClient: ReturnType<typeof useApolloClient>;
};

export const ApolloClientsContext = createContext<ApolloClientsContextType | undefined>(undefined);

export const useApolloClients = () => useContext(ApolloClientsContext);

type ErrorLinkProps = {
  onUnauthorized?: () => void;
};

const createErrorLink = ({ onUnauthorized }: ErrorLinkProps = {}) => {
  return onError(error => {
    if ((error as any)?.networkError?.statusCode === 401) {
      onUnauthorized?.();
    }
  });
};

const createHttpLink = (uri: string) =>
  new HttpLink({
    uri,
    credentials: 'include',
  });

const createAuthLink = () => {
  return setContext(async (_, { headers }) => {
    const { user } = getCachedAuthContext();
    const accessToken = user?.access_token;
    if (accessToken) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${accessToken}`,
          'x-request-id': getRequestId(),
        },
      };
    }
    return { headers };
  });
};

type HandleApiErrorsProps = {
  error?: ApiErrors;
};

const HandleApiErrors = ({ error }: HandleApiErrorsProps) => {
  const history = useHistory();
  const auth = useAuth();

  if (error === 'unauthorized') {
    auth.signinRedirect({
      state: { returnTo: history.location },
    });
  }

  return null;
};

type ApiErrors = 'unauthorized';

export const ApolloClientsProvider = ({ children }: PropsWithChildren<{}>) => {
  const [apiError, setApiError] = React.useState<ApiErrors | undefined>(undefined);

  const handleUnauthorized = () => {
    setApiError('unauthorized');
  };

  const legacyGatewayClient = new ApolloClient({
    link: ApolloLink.from([
      createAuthLink(),
      createErrorLink({
        onUnauthorized: handleUnauthorized,
      }),
      createHttpLink(legacyGatewayUrl),
    ]),
    cache: new InMemoryCache(),
  });

  const federationGatewayClient = new ApolloClient({
    link: ApolloLink.from([
      createAuthLink(),
      createErrorLink({
        onUnauthorized: handleUnauthorized,
      }),
      createHttpLink(federationGatewayUrl),
    ]),
    cache: new InMemoryCache(),
  });

  return (
    <ApolloClientsContext.Provider value={{ legacyGatewayClient, federationGatewayClient }}>
      {children}
      <HandleApiErrors error={apiError} />
    </ApolloClientsContext.Provider>
  );
};

const AuthorizedApolloProvider: React.FC = ({ children }) => {
  const { legacyGatewayClient } = useApolloClients()!;
  const isInIframe = window.self !== window.top;

  // do not render when in iframe as its used for silent auth token renewal
  if (isInIframe) {
    return <></>;
  }

  return <ApolloProvider client={legacyGatewayClient}>{children}</ApolloProvider>;
};

export default AuthorizedApolloProvider;
